import { Button, Dialog } from "@ui5/webcomponents-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { commandFor, resolveShortcutMap, shortcutCommands, type ShortcutCommand, type ShortcutMap } from "./shortcuts";
import "./ShortcutHelp.css";

interface Registration {
  id: number;
  handler: () => void;
  permission: string | undefined;
}

interface ShortcutContextValue {
  map: ShortcutMap;
  register: (command: ShortcutCommand, registration: Registration) => () => void;
  /** Commands with an active handler the user is allowed to run. */
  available: () => ReadonlySet<ShortcutCommand>;
  openHelp: () => void;
}

const ShortcutContext = createContext<ShortcutContextValue | null>(null);

export interface ShortcutProviderProps {
  /** Overrides of the default key map (tenant/user configuration). */
  map?: Partial<ShortcutMap>;
  /** Permission check shared with buttons (ADR-0003/D-4). Default: everything allowed until identity exists. */
  isAllowed?: (permission: string) => boolean;
  children: ReactNode;
}

/**
 * One global keydown listener that dispatches shortcuts to the most recently registered handler of a command
 * (a dialog or detail view opened over a page takes the command; closing it hands it back). A handler whose permission is not granted never runs (PLATFORM-KI-028). The browser
 * default (e.g. Ctrl+P print, Ctrl+F find) is only suppressed when a screen actually handles the command.
 */
export function ShortcutProvider({ map: overrides, isAllowed = () => true, children }: ShortcutProviderProps) {
  const map = useMemo(() => resolveShortcutMap(overrides), [overrides]);
  const registry = useRef(new Map<ShortcutCommand, Registration[]>());
  const allowedRef = useRef(isAllowed);
  const [helpOpen, setHelpOpen] = useState(false);
  const [, setVersion] = useState(0);

  useEffect(() => {
    allowedRef.current = isAllowed;
  }, [isAllowed]);

  const active = useCallback((command: ShortcutCommand): Registration | undefined => {
    const registration = registry.current.get(command)?.at(-1);
    if (!registration) return undefined;
    return registration.permission === undefined || allowedRef.current(registration.permission)
      ? registration
      : undefined;
  }, []);

  const register = useCallback((command: ShortcutCommand, registration: Registration) => {
    const list = registry.current.get(command) ?? [];
    registry.current.set(command, [...list, registration]);
    setVersion((value) => value + 1);
    return () => {
      registry.current.set(
        command,
        (registry.current.get(command) ?? []).filter((item) => item.id !== registration.id),
      );
      setVersion((value) => value + 1);
    };
  }, []);

  const available = useCallback(
    () => new Set(shortcutCommands.filter((command) => command === "help" || active(command) !== undefined)),
    [active],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.repeat) return;
      const command = commandFor(event, map);
      if (!command) return;
      if (command === "help") {
        event.preventDefault();
        setHelpOpen((open) => !open);
        return;
      }
      const hasHandler = (registry.current.get(command)?.length ?? 0) > 0;
      if (!hasHandler) return;
      // The screen owns this command: suppress the browser default even when the user lacks the permission.
      event.preventDefault();
      active(command)?.handler();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [active, map]);

  const value = useMemo<ShortcutContextValue>(
    () => ({
      map,
      register,
      available,
      openHelp: () => {
        setHelpOpen(true);
      },
    }),
    [available, map, register],
  );

  return (
    <ShortcutContext.Provider value={value}>
      {children}
      <ShortcutHelpDialog
        open={helpOpen}
        map={map}
        available={available()}
        onClose={() => {
          setHelpOpen(false);
        }}
      />
    </ShortcutContext.Provider>
  );
}

function useShortcutContext(): ShortcutContextValue {
  const value = useContext(ShortcutContext);
  if (!value) throw new Error("Shortcut hooks must be used inside ShortcutProvider");
  return value;
}

let nextRegistrationId = 1;

export interface UseShortcutOptions {
  enabled?: boolean;
  /** Permission key required to run the command — the same one as the matching button. */
  permission?: string;
}

/** Registers a handler for a command while the calling component is mounted. */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useShortcut(command: ShortcutCommand, handler: () => void, options: UseShortcutOptions = {}): void {
  const { register } = useShortcutContext();
  const { enabled = true, permission } = options;
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(() => {
    if (!enabled) return undefined;
    return register(command, {
      id: nextRegistrationId++,
      handler: () => {
        handlerRef.current();
      },
      permission,
    });
  }, [command, enabled, permission, register]);
}

/** Opens the shortcut help dialog (e.g. from the user menu). */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useShortcutHelp(): { open: () => void; map: ShortcutMap } {
  const { openHelp, map } = useShortcutContext();
  return { open: openHelp, map };
}

/** Human-readable key combination with translated modifier names. */
export function ShortcutKeys({ combo }: { combo: string }) {
  const { t } = useTranslation("ui");
  const parts = combo.split("+").filter((part, index, all) => part !== "" || index === all.length - 1);
  const label = (part: string) => {
    switch (part.toLowerCase()) {
      case "ctrl":
        return t("shortcuts.keys.ctrl");
      case "alt":
        return t("shortcuts.keys.alt");
      case "shift":
        return t("shortcuts.keys.shift");
      case "":
        return "+";
      default:
        return part.length === 1 ? part.toUpperCase() : part;
    }
  };
  return (
    <span className="positive-shortcut-keys" dir="ltr">
      {parts.map((part, index) => (
        <kbd key={`${part}-${String(index)}`}>{label(part)}</kbd>
      ))}
    </span>
  );
}

interface ShortcutHelpDialogProps {
  open: boolean;
  map: ShortcutMap;
  available: ReadonlySet<ShortcutCommand>;
  onClose: () => void;
}

/** Help overlay listing every command and its keys (Ctrl+/). */
function ShortcutHelpDialog({ open, map, available, onClose }: ShortcutHelpDialogProps) {
  const { t } = useTranslation("ui");
  return (
    <Dialog
      open={open}
      headerText={t("shortcuts.title")}
      onClose={onClose}
      data-testid="shortcut-help"
      footer={
        <div className="positive-dialog-footer">
          <Button design="Emphasized" onClick={onClose}>
            {t("actions.close")}
          </Button>
        </div>
      }
    >
      <table className="positive-shortcut-table">
        <thead>
          <tr>
            <th scope="col">{t("shortcuts.command")}</th>
            <th scope="col">{t("shortcuts.keysColumn")}</th>
            <th scope="col">{t("shortcuts.availability")}</th>
          </tr>
        </thead>
        <tbody>
          {shortcutCommands.map((command) => (
            <tr key={command} data-command={command}>
              <td>{t(`shortcuts.commands.${command}`)}</td>
              <td>
                {map[command].map((combo) => (
                  <ShortcutKeys key={combo} combo={combo} />
                ))}
              </td>
              <td>{available.has(command) ? t("shortcuts.available") : t("shortcuts.unavailable")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Dialog>
  );
}
